import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import config from "../../config/config";
import CockpitShell from "../../components/CockpitShell";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { authHeaders, wtNavItems } from "./wtShared";

const Hourglass = ({ progress }) => {
  const top = Math.max(0, 28 * (1 - progress));
  const bottom = Math.max(0, 28 * progress);
  return (
    <svg width="30" height="54" viewBox="0 0 30 54" aria-hidden="true">
      <polygon points="4,2 26,2 15,26" fill="none" stroke="#93c5fd" strokeWidth="1.5" />
      <polygon points="4,52 26,52 15,26" fill="none" stroke="#93c5fd" strokeWidth="1.5" />
      <rect x="8" y="4" width="14" height={top} fill="#22d3ee" opacity="0.85" />
      <rect x="8" y={48 - bottom} width="14" height={bottom} fill="#22d3ee" opacity="0.85" />
      <line x1="15" y1="24" x2="15" y2="30" stroke="#67e8f9" strokeWidth="1.4" />
    </svg>
  );
};

const WtRun = () => {
  const navigate = useNavigate();
  const { attemptId } = useParams();
  const [current, setCurrent] = useState(null);
  const [selected, setSelected] = useState([]);
  const [timeLeft, setTimeLeft] = useState(0);
  const [finished, setFinished] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [hint, setHint] = useState("");
  const autoExpireHandledForQuestionRef = useRef(null);
  const lastQuestionIdRef = useRef(null);

  const loadCurrent = useCallback(async () => {
    try {
      const res = await axios.get(`${config.API_BASE_URL}/api/wt/attempts/${attemptId}/current`, { headers: authHeaders() });
      if (res.data?.completed) {
        setFinished(true);
        setCurrent(res.data);
        setSelected([]);
        setTimeLeft(0);
        return;
      }
      const incomingQuestionId = Number(res.data?.question?.question_id || 0) || null;
      if (incomingQuestionId !== lastQuestionIdRef.current) {
        autoExpireHandledForQuestionRef.current = null;
        lastQuestionIdRef.current = incomingQuestionId;
      }
      setFinished(false);
      setCurrent(res.data);
      setSelected([]);
      setTimeLeft(Number(res.data?.time_left_sec || 0));
      setHint("");
      setError("");
    } catch (e) {
      const msg = e.response?.data?.message || "Testlauf konnte nicht geladen werden.";
      setError(msg);
    }
  }, [attemptId]);

  useEffect(() => {
    document.title = "Experts4Europe | Wissenstest durchführen";
    loadCurrent();
  }, [loadCurrent]);

  useEffect(() => {
    if (finished || !current?.question) return undefined;
    const timer = setInterval(() => {
      setTimeLeft((v) => Math.max(0, v - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [finished, current?.question?.question_id]);

  const autoExpire = useCallback(async () => {
    if (!current?.question?.question_id || busy || finished) return;
    const qid = Number(current.question.question_id);
    if (autoExpireHandledForQuestionRef.current === qid) return;
    autoExpireHandledForQuestionRef.current = qid;
    try {
      setBusy(true);
      const res = await axios.post(
        `${config.API_BASE_URL}/api/wt/attempts/${attemptId}/answer`,
        { question_id: current.question.question_id, selected_option_ids: [] },
        { headers: authHeaders() }
      );
      setHint("Zeit abgelaufen. Naechste Frage.");
      if (res.data?.next_available) {
        await loadCurrent();
      } else {
        setFinished(true);
      }
    } catch (e) {
      // In race conditions a 400 can happen if the question was already finalized once.
      // Reload once and do not loop-post the same timeout answer.
      try {
        await loadCurrent();
      } catch (_reloadErr) {
        setError(e.response?.data?.message || "Zeitablauf konnte nicht verarbeitet werden.");
      }
    } finally {
      setBusy(false);
    }
  }, [attemptId, current?.question?.question_id, busy, finished, loadCurrent]);

  useEffect(() => {
    if (timeLeft !== 0 || finished || !current?.question) return;
    autoExpire();
  }, [timeLeft, finished, current?.question?.question_id, autoExpire]);

  const question = current?.question || null;
  const progress = current?.progress || { index: 0, total: 0 };
  const progressPercent = progress.total > 0 ? Math.round((progress.index / progress.total) * 100) : 0;
  const maxTime = question?.time_limit_sec || 15;
  const timerProgress = Math.min(1, Math.max(0, (maxTime - timeLeft) / Math.max(1, maxTime)));

  const canContinue = useMemo(() => {
    if (!question) return false;
    if (question.type === "single") return selected.length === 1;
    return selected.length > 0;
  }, [question, selected]);

  const toggleAnswer = (optionId) => {
    if (!question || busy || finished) return;
    if (question.type === "single") {
      setSelected([optionId]);
      return;
    }
    setSelected((prev) => (prev.includes(optionId) ? prev.filter((id) => id !== optionId) : [...prev, optionId]));
  };

  const answerAndContinue = async () => {
    if (!question || !canContinue || busy) return;
    try {
      setBusy(true);
      const res = await axios.post(
        `${config.API_BASE_URL}/api/wt/attempts/${attemptId}/answer`,
        { question_id: question.question_id, selected_option_ids: selected },
        { headers: authHeaders() }
      );
      if (res.data?.next_available) {
        await loadCurrent();
      } else {
        setFinished(true);
      }
    } catch (e) {
      setError(e.response?.data?.message || "Antwort konnte nicht gespeichert werden.");
    } finally {
      setBusy(false);
    }
  };

  const submitAttempt = async () => {
    try {
      setSubmitting(true);
      await axios.post(`${config.API_BASE_URL}/api/wt/attempts/${attemptId}/submit`, {}, { headers: authHeaders() });
      navigate(`/assessment/wt/result/${attemptId}`, { replace: true });
    } catch (e) {
      setError(e.response?.data?.message || "Abgabe fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CockpitShell
      title="Wissenstest"
      subtitle="Lies die Frage, waehle eine Antwort und gehe weiter."
      badge="Testlauf"
      navItems={wtNavItems}
    >
      {error ? <p className="rounded-lg border border-rose-300/30 bg-rose-500/10 p-3 text-sm text-rose-100">{error}</p> : null}
      {hint ? <p className="rounded-lg border border-amber-300/30 bg-amber-500/10 p-3 text-sm text-amber-100">{hint}</p> : null}

      {!finished && question ? (
        <Card className="card-e4e">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="font-display text-white">Frage {progress.index} von {progress.total}</CardTitle>
              <div className="flex items-center gap-2 rounded-lg border border-cyan-300/20 bg-slate-950/50 px-2 py-1">
                <Hourglass progress={timerProgress} />
                <Badge className="bg-rose-500/20 text-rose-100">{timeLeft}s</Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <Progress value={progressPercent} className="h-2 bg-slate-800 [&>*]:bg-cyan-400" />
              <p className="text-xs text-slate-300">{progressPercent}% abgeschlossen</p>
            </div>

            <div className="rounded-xl border border-white/10 bg-slate-900/40 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="border-white/20 bg-white/5 text-slate-100">{question.topic || "Wissen"}</Badge>
                <Badge variant="outline" className="border-white/20 bg-white/5 text-slate-100">{question.difficulty || "medium"}</Badge>
              </div>
              <p className="mt-3 text-xl font-semibold text-white">{question.question_text}</p>

              <div className="mt-3 space-y-2">
                {(question.options || []).map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => toggleAnswer(o.id)}
                    className={`w-full rounded-lg border px-3 py-3 text-left ${
                      selected.includes(o.id)
                        ? "border-cyan-300/40 bg-cyan-500/10 text-cyan-100"
                        : "border-white/10 bg-slate-950/30 text-slate-200 hover:bg-white/5"
                    }`}
                  >
                    {o.answer_text}
                  </button>
                ))}
              </div>
              {!canContinue ? <p className="mt-2 text-xs text-slate-300">Bitte waehle eine Antwort, um fortzufahren.</p> : null}
            </div>

            <div className="flex items-center gap-2">
              <Button className="bg-cyan-500 text-slate-950 hover:bg-cyan-300" disabled={!canContinue || busy} onClick={answerAndContinue}>
                {busy ? "Speichert..." : "Weiter"}
              </Button>
            </div>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button type="button" className="text-xs text-slate-300 underline underline-offset-2 hover:text-white">Testlauf verlassen</button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Testlauf wirklich verlassen?</AlertDialogTitle>
                  <AlertDialogDescription>Du kannst den Testlauf spaeter fortsetzen.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                  <AlertDialogAction onClick={() => navigate("/assessment/wt")}>Verlassen</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      ) : null}

      {finished ? (
        <Card className="card-e4e">
          <CardHeader><CardTitle className="font-display text-white">Du bist fertig</CardTitle></CardHeader>
          <CardContent className="space-y-4 text-slate-200">
            <p>Alle Fragen sind beantwortet oder abgelaufen. Gib jetzt den Testlauf final ab.</p>
            <Button className="bg-emerald-400 text-slate-950 hover:bg-emerald-300" onClick={submitAttempt} disabled={submitting}>
              {submitting ? "Wird abgegeben..." : "Final abgeben"}
            </Button>
          </CardContent>
        </Card>
      ) : null}
    </CockpitShell>
  );
};

export default WtRun;
